import { Card, Heading, LoadingSkeleton, StepList } from '@boltenergy-be/design-system'
import styles from 'features/registration/steps/confirmation/Confirmation.module.scss'
import Link from 'components/link/Link'
import { routes } from 'utils/routes'
import { PLENTY_URL } from 'constants/constants'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next/pages'
import { useGetPlentyStatusQuery } from 'store/api/bolt-api'
import { useStoreSelector } from 'hooks/store'
import ConfirmationWrapper from './ConfirmationWrapper'
import { PlentySfPaymentMethod } from 'store/api/types'
import { Producer } from 'types/producer'

const IsFromPlentyFlowView = ({ producer }: { producer: Producer }) => {
  // Redux state
  const { plentyOpportunityId } = useStoreSelector((store) => store.marketing)

  // Redux query
  const { data, isLoading } = useGetPlentyStatusQuery(plentyOpportunityId, { skip: !plentyOpportunityId })
  const paidPlentyWithPaymentEngine = data?.paymentMethod === PlentySfPaymentMethod.PAYMENT_ENGINE

  // Next router
  const { locale } = useRouter()

  // i18n
  const { t } = useTranslation('registration')

  return (
    <ConfirmationWrapper limitedWidth>
      <header>
        {isLoading ? (
          <LoadingSkeleton>
            <LoadingSkeleton.Title />
            <LoadingSkeleton.Paragraph lines={1} />
          </LoadingSkeleton>
        ) : (
          <>
            <Heading as="h1" variant="h2">
              {t(`confirmation.fromPlenty.title`)}
            </Heading>
            <p>{t(`confirmation.fromPlenty.description`)}</p>
          </>
        )}
      </header>

      <Card>
        {isLoading ? (
          <LoadingSkeleton className="gap-600">
            <LoadingSkeleton.Rectangle width="100%" height={58} />
            <LoadingSkeleton.Rectangle width="100%" height={58} />
            <LoadingSkeleton.Rectangle width="100%" height={58} />
            <LoadingSkeleton.Rectangle width="100%" height={58} />
          </LoadingSkeleton>
        ) : (
          <StepList size="large" className={styles['step-list']}>
            <li key={`confirmation.fromPlenty-${data?.paymentMethod}-dynamic`}>
              <div>
                <strong>{t(`confirmation.fromPlenty.${paidPlentyWithPaymentEngine ? 'paymentEngine' : 'bankTransfer'}.title`)}</strong>
                <span>{t(`confirmation.fromPlenty.${paidPlentyWithPaymentEngine ? 'paymentEngine' : 'bankTransfer'}.description`)}</span>
              </div>
            </li>
            <>
              {(
                t(`confirmation.fromPlenty.list`, { returnObjects: true, producer: producer.name[locale] }) as {
                  title: string
                  description: string
                }[]
              ).map((item, index) => (
                <li key={`confirmation.fromPlenty-${paidPlentyWithPaymentEngine}-${index}`}>
                  <div>
                    <strong>{item.title}</strong>
                    <span>{item.description}</span>
                  </div>
                </li>
              ))}
            </>
          </StepList>
        )}
      </Card>

      {!isLoading && <small className={styles.close}>{t('confirmation.closeWindow')}</small>}

      {!isLoading && (
        <div className={styles['plenty-actions']}>
          <Link representation="button" href={routes(locale).home}>
            {t('confirmation.actions.toBolt')}
          </Link>

          <Link representation="button" as="a" href={`${PLENTY_URL}?simulation_so=Bolt%20website`}>
            {t('confirmation.actions.toPlenty')}
          </Link>
        </div>
      )}
    </ConfirmationWrapper>
  )
}

export default IsFromPlentyFlowView
